import Modal from '../../general/components/Modal';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { useAppStoreDispatch, useAppStoreSelector } from '../../../store';
import { languageEvent } from '../../../events/languageEvent';
import { ChangeEvent, useRef, useState } from 'react';
import { toggleNewPageSwitch } from '../../settings/store';
import axios from 'axios';
import User from '../models/user';
import { useToast } from '../../general/components/Toast.hook';

interface UserSettingsProps {
  isModalShow: boolean;
  setIsModalShow: (show: boolean) => void;
  resetPassword?: boolean;
  setResetPassword?: (show: boolean) => void;
}

const UserSettings = ({ isModalShow, setIsModalShow, resetPassword, setResetPassword }: UserSettingsProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppStoreDispatch();
  const [error, setError] = useState<string>('');
  const settingsState = useAppStoreSelector((state) => state.settings.data);
  const userState = useAppStoreSelector((state) => state.user.data);
  const token = User.loadUserToken();
  const [triggerMessageHandler] = useToast();
  // const portalState = useAppStoreSelector((state) => state.portal);

  const onLanguageSelectChangeHandler = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    event.preventDefault();
    const selectedLanguage = event.target.value;
    i18n.changeLanguage(selectedLanguage);
    window.dispatchEvent(languageEvent(selectedLanguage));
    // portalState.data.languageEvent.next(selectedLanguage);
  };

  const onNewPageSwitchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    // console.log(event.target.checked);
    dispatch(toggleNewPageSwitch(event.target.checked));
  };

  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  const validatePassword = (password: string) => {
    const passwordLengthValid = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);

    if (!passwordLengthValid) {
      return 'Password must be at least 8 characters long.';
    }
    if (!hasUppercase) {
      return 'Password must contain at least one uppercase letter.';
    }
    return ''; // No error
  };

  const handleClick = async () => {
    const password = passwordRef.current ? passwordRef.current.value : '';
    const confirmPassword = confirmPasswordRef.current ? confirmPasswordRef.current.value : '';

    // Reset errors on each click
    setError('');

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return; // Stop further processing if validation fails
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    const username = userState.user?.username;
    const reset = userState.user?.password_reset;
    await axios.post('/api/user/updatePassword', { username, password, reset: reset }, {headers: {"Authorization" : `Bearer ${token}`}}).then((res) => {
      if (res.data.success) {
        setIsModalShow(false);
        triggerMessageHandler("Password changed", false);
        setResetPassword?.(true);
      } else {
        console.error('Error: ', res.data.message);
      }
    });
  };

  return (
    <Modal
      show={isModalShow}
      onHide={() => setIsModalShow(false)}
      title={t('settings.userSettings')}
      footer={null}
    >
      <Stack spacing={3}>
        <FormControl fullWidth>
          <FormLabel>{t('settings.language')}</FormLabel>
          <Select
            onChange={(e) => onLanguageSelectChangeHandler(e as any)}
            value={i18n.language}
          >
            <MenuItem value="en">EN</MenuItem>
            <MenuItem value="de">DE</MenuItem>
          </Select>
          <FormControlLabel
            control={
              <Switch
                checked={settingsState?.isNewPageChecked}
                onChange={onNewPageSwitchHandler}
              />
            }
            label="Open portals in new page"
            sx={{ mt: 2 }}
          />
        </FormControl>
        <FormControl fullWidth>
          <FormLabel>Change Password</FormLabel>
          {resetPassword && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              Please change your password
            </Typography>
          )}
          <TextField
            label="New Password"
            type="password"
            inputRef={passwordRef}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          <TextField
            label="Confirm Password"
            type="password"
            inputRef={confirmPasswordRef}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
          <Button 
            variant="contained" 
            onClick={handleClick}
            sx={{ mt: 2 }}
          >
            Apply
          </Button>
        </FormControl>
      </Stack>
    </Modal>
  );
};

export default UserSettings;
